// client/src/themes.js
//
// THE ENCATAR THEME — one set of rules, four palettes to spend them on.
//
// The RULES live here; the COLOURS live in ./theme/palettes. Every value below
// is reached through a role — the chrome, the action colour, the accent, the
// pass/fail pair — so choosing a different palette repaints the app without
// rewriting a line of this file. That is why there can be four of these and
// still one design.
//
// ONE OF THE FOUR IS DARK, and it is a real scheme rather than an inversion:
// `mode` comes from the palette, so MUI's own defaults follow it. The handful of
// places below that branch on `p.dark` are the ones where a rule that is right
// on a light ground is not merely dimmer on a dark one but backwards — a field
// that should sit above its card rather than below it, a shadow that must be
// black rather than navy, a tooltip that must contrast with the page.
//
// The rules themselves come from `client/public/encatar_classic_palette.jsx`,
// and its guidance is followed rather than reinterpreted:
//
//   SLATE 900 REPLACES PURE BLACK for body text. Its faint blue cast harmonises
//   with the brand blues; true black beside them looks like a different design.
//
//   FOREST GREEN REPLACES CLINICAL GREEN and BURGUNDY REPLACES BRIGHT RED for
//   pass/fail and success/danger. A failing mark should read as serious, not as
//   an alarm, and #881337 does that where #DC2626 shouts.
//
// WHITE PAGE, BLUE CARDS — and this one DEPARTS from the palette file, which
// argues for ivory as paper against white as screen.
//
// That argument holds on a wide desktop, where a card floats in a broad margin
// of the ground. It does not hold on a phone, where the card is nearly the full
// width and the ivory survives as a 12px frame down each side — paid for on
// every screen and delivered on none. The phone moved first, and the two
// clients have to be one product, so the ground is white here too and the CARDS
// carry the colour: a pale royal tint with a royal 100 edge.
//
// GOLD IS ORNAMENT, NOT A CONTROL. It stays `secondary` here — the web has
// always spent it on achievement rather than on buttons, and MUI does not route
// tonal buttons through that slot the way Material Design 3 does on the phone.
// Its jobs are the hairline under every card header, the report card masthead
// and the second chart series.
//
// THE CHROME IS ONE FLAT COLOUR — the palette's `deep`, the sidebar and the top
// bar, with no gradient in either. See the note on SIDEBAR.bg for why that
// matters.
//
// THE FIFTEEN THEMES ARE GONE, and the four that replaced them are not the same
// thing. Those were palette-only variations that gave a school fifteen ways to
// look slightly different and no way to look right. These four are complete
// schemes — each with its own chrome, ink, semantics and ground — and the
// picker offers exactly the choice a school actually has: a house colour, and
// whether the app is read in the dark.

import { createTheme, alpha } from '@mui/material/styles';
import {
  PALETTES, DEFAULT_PALETTE_ID, PALETTE_STORAGE_KEY, paletteById,
  chromeFor, categoricalFor, isLight, inkOn,
} from './theme/palettes';

export { PALETTES, DEFAULT_PALETTE_ID, PALETTE_STORAGE_KEY, paletteById, categoricalFor, isLight };

/**
 * The families, verbatim from the palette file. ★ marks the shade that family
 * leads with, and those are the ones the theme below actually spends.
 */
export const C = {
  indigo: {}, royal: {}, sky: {}, gold: {}, ivory: {},
  slate: {}, forest: {}, burg: {},
};

/**
 * Rewrite `C` in place from a palette's roles.
 *
 * The shade numbers are a LADDER rather than fixed colours: 700 is always the
 * deepest and 50 the faintest, whichever palette supplies them. A page asking
 * for `royal 700` is asking for the chrome. Byte-for-byte the same mapping the
 * phone uses (apps/mobile/src/theme.js), so the two clients cannot disagree
 * about what a shade means.
 */
function buildC(p) {
  Object.assign(C.royal, {
    700: p.deep, 600: p.mid, 500: p.mid, 400: p.soft, 100: p.border, 50: p.tint,
  });
  Object.assign(C.indigo, {
    900: p.deep, 800: p.deep, 700: p.mid, 600: p.soft, 100: p.tint, 50: p.tint,
  });
  Object.assign(C.sky, {
    700: p.info, 600: p.info, 500: p.soft, 400: p.soft, 100: p.infoBg, 50: p.infoBg,
  });
  Object.assign(C.gold, {
    800: p.accentDeep, 700: p.accentDeep, 600: p.accentDeep, 500: p.accent,
    400: p.accentSoft, 300: p.accentSoft, 200: p.tintAccent,
    100: p.tintAccent, 50: p.tintAccent,
  });
  Object.assign(C.ivory, {
    warm: p.border, parchment: p.tint, cream: p.tint, main: p.bg,
  });
  Object.assign(C.slate, {
    900: p.text, 800: p.text, 700: p.muted, 600: p.muted, 500: p.muted,
    400: p.muted, 300: p.border, 200: p.border, 100: p.tint,
  });
  // SEMANTIC, and deliberately not brand-derived: pass and fail must mean the
  // same thing in every palette.
  Object.assign(C.forest, {
    800: p.successTx, 700: p.successTx, 600: p.success, 500: p.success,
    200: p.successBg, 100: p.successBg, 50: p.successBg,
  });
  Object.assign(C.burg, {
    900: p.dangerTx, 800: p.dangerTx, 700: p.danger, 600: p.danger,
    200: p.dangerBg, 100: p.dangerBg, 50: p.dangerBg,
  });
}

/** The header/sidebar pairing, named so it cannot be mixed by accident. */
/**
 * THE CHROME — the sidebar and the top bar, derived rather than written down.
 *
 * These are the two surfaces someone sees on every screen, and they are a DEEP
 * ground with a LIGHT foreground, the inverse of everything else; reaching for
 * the ordinary text colour here gives near-black on a near-black bar.
 * `chromeFor` derives the whole set from the palette's `deep`, which is what
 * makes adding a fifth palette a row of colours rather than a second set of
 * decisions — and it is the SAME function the phone calls, so both clients draw
 * the same bar.
 *
 * Rebuilt in place by `buildTheme`: DashboardLayout imports this object by name,
 * and replacing it would leave the layout holding the previous palette's chrome.
 */
export const SIDEBAR = {};

/**
 * BUILD THE WHOLE THEME FROM ONE PALETTE.
 *
 * Everything below depends on which of the four is chosen, so it lives inside
 * this function rather than at module scope. `C` and `SIDEBAR` are refilled
 * FIRST and IN PLACE: pages import those two objects by name, and handing back
 * new ones would leave every importer painting the previous palette.
 *
 * Memoised, because MUI compares themes by identity — returning a fresh object
 * on every render would restyle the entire tree on every keystroke.
 */
const _built = new Map();

export function buildTheme(paletteId) {
  const p = paletteById(paletteId);
  if (_built.has(p.id)) {
    // Still refill the shared objects: another palette may have been built
    // since, and these are the copies every page holds.
    buildC(p);
    Object.assign(SIDEBAR, chromeFor(p));
    return _built.get(p.id);
  }

  buildC(p);
  Object.assign(SIDEBAR, chromeFor(p));


  const ink = C.slate[900];
  // SLATE 600, not 500. Secondary text has to clear 4.5:1 against the WORST
  // ground it lands on, and that is the tinted card — where slate 500 is 4.32:1.
  // Checking it against white alone would have passed it.
  const inkSoft = C.slate[600];

  /**
   * THE CARD — a pale blue sheet on a white page, with a royal edge.
   *
   * The same three values the phone uses (apps/mobile/src/theme.js `CARD`), so a
   * card is one shape across the two clients rather than a colour each of them
   * chose separately.
   */
  const CARD_BORDER = C.royal[100];
  // Blue-tinted rules, not grey ones. Same lightness as the slate they replace,
  // so nothing got heavier or lighter — only bluer.
  const line = p.border;
  const inputLine = p.border;

  /**
   * A soft shadow ramp, tinted with the CARD'S OWN BLUE.
   *
   * Not with the slate ink and not with black: over a white ground a neutral grey
   * shadow reads as smudge, where a blue-cast one reads as the same sheet lifting
   * off the page. Wide rather than tight, for the same reason.
   */
  //
  // ON A DARK PALETTE IT IS BLACK AND MUCH STRONGER. A shadow works by being
  // darker than the surface it falls on, and on a dark scheme the chrome navy is
  // already close to the page — tinting with it at 6% produces nothing at all.
  // Black at a third opacity is the only value that still separates a card from
  // the ground.
  const shadowTint = p.dark ? '#000000' : C.royal[700];
  const shadowBase = p.dark ? 0.30 : 0.06;
  const softShadows = (() => {
    const s = ['none'];
    for (let i = 1; i <= 24; i += 1) {
      const y = Math.round(i * 0.8) + 1;
      const blur = Math.round(i * 2.2) + 4;
      s.push(
        `0px ${y}px ${blur}px ${Math.round(i * 0.1)}px ${alpha(shadowTint, shadowBase + i * 0.005)}, `
        + `0px ${Math.round(y / 2)}px ${Math.round(blur / 2)}px ${alpha(shadowTint, shadowBase * 0.7)}`
      );
    }
    return s;
  })();

  // UI text is Inter; the display sizes are a serif, which is where the
  // "classic" comes from. Only h1-h3 take it — a dense table of marks set in a
  // serif is harder to scan, and the palette's own report card makes the same
  // split, using Georgia for the document and system-ui for the data.
  const SANS = ['"Inter"', '"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(',');
  const SERIF = ['"Source Serif 4"', 'Georgia', '"Times New Roman"', 'serif'].join(',');

  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

  const palette = {
    // THE BASE SCHEME, from the palette rather than assumed.
    //
    // This one word decides far more than the word suggests: MUI derives every
    // default it was not given from it — disabled text, ripple opacity, the
    // divider it falls back to, the elevation overlay on a raised surface, the
    // ink inside a Select's dropdown. Handing a dark palette to a light scheme
    // gives dark grounds with MUI's own light-scheme defaults printed on them,
    // which is the difference between a dark theme and a broken one.
    mode: p.dark ? 'dark' : 'light',

    // ROYAL is the action colour — the palette's own "Primary Action" button.
    // Indigo is reserved for headers and the sidebar, where it is paired with
    // gold; using it for buttons too would leave nothing to distinguish chrome
    // from action.
    // The ink is ASKED FOR rather than assumed to be white. Three of the four
    // palettes have a deep primary that carries white; the dark one's is a lifted
    // blue that does not, and white on it is the one contrast failure a dark
    // theme reliably ships with.
    primary: {
      main: C.royal[500], dark: C.royal[700], light: C.royal[400], contrastText: inkOn(p, C.royal[500]),
    },
    // GOLD is achievement, not a second action colour. Its ink is dark — white
    // on gold fails badly — but MEASURED rather than set to the chrome navy,
    // which is what it used to be: Blue & White has no gold, its accent slot is
    // an azure, and navy on azure is 2.41:1.
    secondary: {
      main: C.gold[500], dark: C.gold[700], light: C.gold[300], contrastText: inkOn(p, C.gold[500]),
    },
    success: { main: C.forest[600], dark: C.forest[800], light: C.forest[500], contrastText: inkOn(p, C.forest[600]) },
    error: { main: C.burg[700], dark: C.burg[900], light: C.burg[600], contrastText: inkOn(p, C.burg[700]) },
    // THE PALETTE'S WARNING INK, not its accent. The accent is a brand colour
    // that happens to be gold in three of the four, and its deepest rung tops
    // out at 4.35:1 under either ink — close enough to look fine and not close
    // enough to ship. Every palette states a warning ink outright (#8A5E1D and
    // friends), which clears it at 5.5 and is unmistakably the same gold.
    warning: { main: p.warningTx, dark: C.gold[800], light: C.gold[500], contrastText: inkOn(p, p.warningTx) },
    // SKY IS A LIGHT FAMILY on three palettes and a mid blue on the fourth, so
    // its ink is measured too. Deep indigo was hardcoded here, which is the
    // chrome — and on every palette whose chrome and info are both blue that is
    // one blue on another at under 2.1:1.
    info: { main: C.sky[600], dark: C.sky[700], light: C.sky[400], contrastText: inkOn(p, C.sky[600]) },

    // THE PAGE IS WHITE, AND THE CARDS ARE BLUE — the inverse of what it was, and
    // the same swap the phone made.
    //
    // The ground was ivory and the cards were white, so the page carried the
    // colour and the content carried none. That reads well in a wide desktop
    // margin and not at all anywhere else, and it left the two clients looking
    // like different products once the phone moved.
    //
    // `paper` rather than only `MuiCard`, deliberately: half the app's content
    // blocks are `<Paper>` and half are `<Card>`, so tinting one and not the
    // other would give the app two kinds of card depending on which component a
    // page happened to reach for. Dialogs, menus and popovers take the tint with
    // them, which is right — they are surfaces too. Text fields set their own
    // white ground (see MuiOutlinedInput), so an input still reads as a slot cut
    // INTO the card.
    background: {
      default: p.bg,           // the page
      paper: p.card,           // every surface that sits on it
      tint: C.royal[50],       // alternating rows, inset panels
      parchment: C.royal[50],
    },
    text: { primary: ink, secondary: inkSoft, disabled: C.slate[400] },
    divider: line,
    grey: {
      50: C.slate[100], 100: C.slate[100], 200: C.slate[200], 300: C.slate[300],
      400: C.slate[400], 500: C.slate[500], 600: C.slate[600], 700: C.slate[700],
      800: C.slate[800], 900: C.slate[900],
    },
  };

  const built = createTheme({
  palette,
  shape: { borderRadius: 12 },
  shadows: softShadows,

  typography: {
    fontFamily: SANS,
    h1: { fontFamily: SERIF, fontWeight: 700, letterSpacing: '-0.02em', fontSize: '2.5rem' },
    h2: { fontFamily: SERIF, fontWeight: 700, letterSpacing: '-0.018em', fontSize: '2rem' },
    h3: { fontFamily: SERIF, fontWeight: 700, letterSpacing: '-0.015em', fontSize: '1.75rem' },
    h4: { fontWeight: 700, letterSpacing: '-0.018em', fontSize: '1.5rem' },
    h5: { fontWeight: 700, letterSpacing: '-0.015em', fontSize: '1.25rem' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.0625rem' },
    subtitle1: { fontWeight: 600, letterSpacing: '-0.005em' },
    subtitle2: { fontWeight: 600 },
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: '0.01em', textTransform: 'none' },
    overline: { fontWeight: 700, letterSpacing: '0.09em' },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Motion is short and optional. Collapsed once, globally, rather than
        // remembered at each animation.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        body: { backgroundColor: palette.background.default },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(inkSoft, 0.3),
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(inkSoft, 0.5) },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingInline: 18,
          paddingBlock: 8,
          transition: `background-color 160ms ${EASE}, box-shadow 160ms ${EASE}, transform 160ms ${EASE}, border-color 160ms ${EASE}`,
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)' },
        },
        contained: {
          boxShadow: `0 1px 2px ${alpha(ink, 0.12)}`,
          '&:hover': { boxShadow: `0 6px 18px ${alpha(C.royal[500], 0.28)}` },
        },
        containedSecondary: {
          '&:hover': { boxShadow: `0 6px 18px ${alpha(C.gold[600], 0.34)}` },
        },
        outlined: { borderWidth: 1.5, '&:hover': { borderWidth: 1.5 } },
        sizeSmall: { paddingInline: 12, paddingBlock: 5 },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: `background-color 160ms ${EASE}, color 160ms ${EASE}, transform 160ms ${EASE}`,
          '&:hover': { transform: 'translateY(-1px)', backgroundColor: alpha(C.royal[500], 0.08) },
          '&:active': { transform: 'translateY(0)' },
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 14,
          // Royal 100, so the card's edge belongs to the same family as its
          // fill. A grey border round a blue card is the join showing.
          border: `1px solid ${CARD_BORDER}`,
          backgroundImage: 'none',
          transition: `box-shadow 200ms ${EASE}, border-color 200ms ${EASE}`,
          '&:hover': {
            boxShadow: `0 10px 30px ${alpha(C.royal[700], 0.10)}`,
            borderColor: alpha(C.royal[500], 0.38),
          },
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          paddingBottom: 8,
          // The gold hairline under a card header is the palette's signature —
          // the same rule that runs under the report card's masthead.
          borderBottom: `2px solid ${alpha(C.gold[500], 0.28)}`,
          marginBottom: 8,
        },
        title: { fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.01em' },
        subheader: { fontSize: '0.8125rem', lineHeight: 1.5 },
      },
    },

    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' }, rounded: { borderRadius: 14 } },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backdropFilter: 'saturate(180%) blur(12px)',
          borderBottom: `1px solid ${line}`,
        },
      },
    },

    MuiDrawer: { styleOverrides: { paper: { backgroundImage: 'none', borderRight: 'none' } } },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 8,
          transition: `background-color 150ms ${EASE}, padding-left 150ms ${EASE}`,
          '&:hover': { backgroundColor: alpha(C.royal[500], 0.08), paddingLeft: 20 },
          '&.Mui-selected': {
            backgroundColor: alpha(C.royal[500], 0.14),
            '&:hover': { backgroundColor: alpha(C.royal[500], 0.2) },
          },
        },
      },
    },

    MuiTextField: { defaultProps: { size: 'small' } },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          // A field is a slot cut INTO the card, so it needs a ground one step
          // away from the card's. On the light palettes that is white against a
          // tinted card; on the dark one white would be a hole burned through the
          // page, so it goes the other way and takes the raised tint — the card
          // is #181E27 and the field #212936, which is the same one-step
          // relationship read in the direction a dark scheme reads it.
          backgroundColor: p.dark ? p.tint : '#FFFFFF',
          transition: `box-shadow 150ms ${EASE}, border-color 150ms ${EASE}`,
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(C.royal[500], 0.5) },
          // A focus RING, not a thicker border: it does not change the field's
          // metrics, so nothing reflows as you tab through a form.
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(C.royal[500], 0.16)}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1.5 },
        },
        notchedOutline: { borderColor: inputLine },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,     // the palette's badges are pills
          fontWeight: 600,
          transition: `background-color 150ms ${EASE}, transform 150ms ${EASE}`,
        },
        // A DEFINITE GROUND AND A DEFINITE INK.
        //
        // MUI's default filled chip is a near-transparent grey derived from the
        // text colour. On the old white card that was faint but legible; on the
        // pale blue one the cards now carry it is barely a shape at all, and
        // the label reads as sunk into the card rather than sitting on a chip.
        // The help centre shows it worst — every topic carries a row of tag
        // chips — but it was true of every chip in the app.
        //
        // Royal 50 under royal 700 is 10.4:1, and it is the same pairing the
        // phone uses for a chip and for a selected tab, so a tag looks the same
        // on both clients. Only the DEFAULT (`colorDefault`) is touched: a chip
        // given color="success" or color="error" is saying something, and must
        // keep saying it.
        // ON THE DARK PALETTE THE TWO ENDS OF THAT PAIRING COLLAPSE.
        //
        // Royal 50 is the palette's `tint` and royal 700 is its `deep`, and on
        // the dark scheme those are #212936 and #181E27 — a chip and its own
        // label at 1.06:1, which is a tag nobody can read. Every table in class
        // management labels its rows with these, so those pages lost their tags
        // entirely.
        //
        // The ground becomes a wash of the action colour over the card, and the
        // ink comes from the LIGHT end of the ramp, exactly as the phone's
        // tonal button does.
        filled: {
          '&.MuiChip-colorDefault': {
            backgroundColor: p.dark ? alpha(p.mid, 0.22) : C.royal[50],
            color: p.dark ? p.soft : C.royal[700],
            '& .MuiChip-deleteIcon': { color: alpha(p.dark ? p.soft : C.royal[700], 0.6) },
            '&.MuiChip-clickable:hover': {
              backgroundColor: p.dark ? alpha(p.mid, 0.32) : C.royal[100],
            },
          },
        },
        outlined: { borderWidth: 1.5 },
        clickable: { '&:hover': { transform: 'translateY(-1px)' } },
      },
    },

    // THE TABLE HEAD IS SLATE 900 with white text, exactly as the report card
    // draws it. It is the strongest classic signal in the whole system and it
    // costs nothing: a dark rule across the top of a table separates headings
    // from data better than any weight of grey.
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            // The chrome's own navy, so the bar above a table is the same blue
            // as the bar above the page. It was slate 900 — a near-black that
            // belonged to no family on the screen once the ground went white.
            backgroundColor: C.royal[700],
            color: inkOn(p, C.royal[700]),
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            borderBottom: 'none',
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color 140ms ${EASE}`,
          // Alternating rows, a hair of royal. Ivory cream before — a warm band
          // inside a cool card reads as a mistake rather than as a ledger rule.
          '&:nth-of-type(even)': { backgroundColor: alpha(C.royal[500], 0.045) },
          '&:last-child td': { borderBottom: 0 },
        },
        hover: { '&:hover': { backgroundColor: alpha(C.royal[500], 0.06) } },
      },
    },

    MuiTableCell: { styleOverrides: { root: { borderColor: line } } },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { height: 3, borderRadius: 3, backgroundColor: C.gold[500] },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          fontWeight: 600,
          borderRadius: '8px 8px 0 0',
          transition: `color 150ms ${EASE}, background-color 150ms ${EASE}`,
          '&:hover': { backgroundColor: alpha(C.royal[500], 0.06) },
          '&.Mui-selected': { color: C.indigo[700] },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: `0 24px 64px ${alpha(ink, 0.24)}` },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '1.125rem',
          borderBottom: `2px solid ${alpha(C.gold[500], 0.3)}`,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
        standardSuccess: { backgroundColor: C.forest[50], color: C.forest[800] },
        standardError: { backgroundColor: C.burg[50], color: C.burg[900] },
        // The warning PAIR, not the gold ladder. Gold 50/800 is the accent's
        // tint under the accent's ink, which is a brand pairing that happens to
        // look like a warning on the light palettes; every palette states the
        // real one, and on the dark scheme they are the only values that work.
        standardWarning: { backgroundColor: p.warningBg, color: p.warningTx },
        // Royal, not sky: sky 700 on sky 50 is 4.06:1. Royal 700 stays in the
        // blue family and clears it at 8.35 — but royal 700 IS the chrome, and
        // on the dark palette the chrome and the info ground are the same near
        // black, so there it has to take the info pair's own ink instead.
        standardInfo: { backgroundColor: C.sky[50], color: p.dark ? p.infoTx : C.royal[700] },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
          padding: '6px 10px',
          // A tooltip is the one surface that must contrast with the PAGE, not
          // with the card. Slate 900 is the body ink — near-black on the light
          // palettes and a near-white on the dark one, where a tooltip painted
          // in it would carry MUI's white label on a white ground. On dark it
          // takes the raised surface and states its ink outright.
          backgroundColor: p.dark ? p.tint : alpha(C.slate[900], 0.95),
          color: p.dark ? p.text : '#FFFFFF',
          border: p.dark ? `1px solid ${p.border}` : 'none',
        },
      },
    },

    MuiAvatar: { styleOverrides: { root: { fontWeight: 700 } } },
    MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999, height: 8 } } },

    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
        track: { borderRadius: 999, opacity: 0.3 },
        thumb: { boxShadow: `0 1px 3px ${alpha(ink, 0.3)}` },
      },
    },

    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${line}`,
          borderRadius: 10,
          overflow: 'hidden',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { borderColor: alpha(C.gold[500], 0.45) },
        },
      },
    },
  },
  });

  _built.set(p.id, built);
  return built;
}


/**
 * Kept so callers that still pass a stored theme name keep working.
 *
 * Schools have a name saved in their settings from when there were fifteen;
 * every one of them now resolves here, which is the point.
 */
export const getThemeByName = (name) => buildTheme(name);
export const applyTheme = (name) => buildTheme(name);

// BUILT AT MODULE LOAD, and the result deliberately not kept: the point is the
// SIDE EFFECT. `buildTheme` fills `C` and `SIDEBAR` in place, so a module that
// reads either of those before any provider has mounted gets real colours
// rather than an empty object.
buildTheme(DEFAULT_PALETTE_ID);

/**
 * The four, in the shape the appearance picker reads.
 *
 * Derived from PALETTES rather than written out again — a fifth palette should
 * appear in the picker by existing, not by being registered twice.
 */
export const themeMetadata = PALETTES.reduce((acc, pal) => {
  acc[pal.id] = {
    name: pal.name,
    description: pal.tagline,
    color: pal.deep,
    accent: pal.accent,
    mid: pal.mid,
    tint: pal.tint,
    // So the picker can draw a dark palette's swatch on its own ground. Three
    // pale chips on a white card is a fair preview of three light schemes and a
    // lie about the fourth.
    dark: !!pal.dark,
  };
  return acc;
}, {});

const themes = PALETTES.reduce((acc, pal) => { acc[pal.id] = () => buildTheme(pal.id); return acc; }, {});
export default themes;
